import { type SlotsToClasses } from "@heroui/react";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { Brand, type BrandProps } from "../base/Brand";
import { Collapse, type CollapseProps } from "../base/Collapse";
import type { ContainerProps, LayoutSettings } from '../types';
import { SidebarMenus, type SidebarMenuItemsType, type SidebarMenusProps } from "./SidebarMenus";

export interface VGroveSidebarProps extends ContainerProps {
    isCollapsed?: boolean;
    settings: LayoutSettings;
    collapse?: React.ReactNode | false;
    items: SidebarMenuItemsType;
    classNames?: SlotsToClasses<"base" | "items" | "actions">;
    actions?: ((isCollapsed?: boolean) => React.ReactNode) | React.ReactNode;
    menusProps?: Omit<SidebarMenusProps, "items" | "isCollapsed">;
    brand?: Omit<BrandProps, "isCollapsed">;
    onCollapsedChange?: CollapseProps['onCollapsedChange'];
}

export const VGroveSidebar: React.FC<VGroveSidebarProps> = ({
    items,
    brand,
    actions,
    settings,
    collapse,
    className,
    classNames,
    isCollapsed: propIsCollapsed,
    menusProps,
    onCollapsedChange
}) => {
    const sidebarConfig = settings.sidebarConfig;
    const responsiveConfig = settings.responsive;
    const [isCollapsed, setCollapsed] = useState(propIsCollapsed ?? sidebarConfig?.defaultCollapsed ?? true);
    const [isMobile, setIsMobile] = useState(false);

    // 同步外部传入的 isCollapsed 状态
    useEffect(() => {
        if (propIsCollapsed !== undefined) {
            setCollapsed(propIsCollapsed);
        }
    }, [propIsCollapsed]);

    // 响应式检测
    useEffect(() => {
        if (responsiveConfig?.hideSidebarOnMobile) {
            const handleResize = () => {
                const breakpoint = responsiveConfig.mobileBreakpoint || '768px';
                const breakpointValue = parseInt(breakpoint.replace('px', ''));
                const mobile = window.innerWidth < breakpointValue;

                setIsMobile(mobile);
                if (mobile) {
                    setCollapsed(true);
                }
            };

            handleResize();
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }
    }, [responsiveConfig]);

    const handleCollapseChange: CollapseProps['onCollapsedChange'] = (isCollapsed) => {
        setCollapsed(isCollapsed);
        onCollapsedChange?.(isCollapsed);
    }

    const renderCollapse = () => {
        if (collapse)
            return collapse;

        return (
            <Collapse
                collapse={isCollapsed}
                onCollapsedChange={handleCollapseChange}
            />
        )

    }

    // 如果在移动端且配置了隐藏，则不显示侧边栏
    if (isMobile && responsiveConfig?.hideSidebarOnMobile) {
        return null;
    }

    return (
        <div
            className={
                clsx(
                    "transition-width",
                    "flex",
                    "flex-col",
                    "justify-between",
                    "border-r-small",
                    "border-default-100",
                    "fixed",
                    "top-0",
                    "bottom-0",
                    // 响应式类名
                    {
                        "items-center px-2 py-4": isCollapsed,
                        "px-2 py-4": !isCollapsed,
                        "hidden md:flex": responsiveConfig?.hideSidebarOnMobile,
                        "flex": !responsiveConfig?.hideSidebarOnMobile,
                    },
                    className,
                    classNames?.base
                )
            }
            style={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'unset',
                insetBlockStart: settings.header ? settings.navbar?.height : "0",
                width: isCollapsed ? '64px' : (sidebarConfig?.width || '280px'),
                minWidth: isCollapsed ? '64px' : (sidebarConfig?.width || '280px'),
            }}
        >
            <div className="flex justify-between items-center">
                {brand?.name && (
                    <Brand
                        isCollapsed={isCollapsed}
                        classNames={{ logo: "w-9 h-9" }}
                        {...brand}
                    />
                )}
            </div>
            <SidebarMenus
                isCollapsed={isCollapsed}
                items={items}
                {...menusProps}
            />
            <div className={clsx(
                "flex gap-2 items-center",
                {
                    "flex-col justify-center": isCollapsed,
                    "flex-row": !isCollapsed,
                    "justify-between": !isCollapsed,
                },
                classNames?.actions)}
            >
                {typeof actions === "function" ? actions(isCollapsed) : actions}
                {renderCollapse()}
            </div>
        </div>
    )
}